import { ScholarshipServiceIntegration } from '@/infrastructure/scholarship-service/scholarship-service-integration';
import { IScholarship } from '@studyportals/sp-scholarships-api-interface';
import { ResponseError } from 'superagent';
import { Scholarship } from './scholarship';
import CountryPresenter from './country-presenter';
import DisciplinePresenter from './discipline-presenter';
import StatePresenter from './state-presenter';
import { ScholarshipSearchOutcome } from './scholarship-search-outcome';
import { UTCDateTime } from '@studyportals/mb-platform-date-time';
import cookieHandler from '@/infrastructure/cookie-handler';

class ScholarshipPresenter extends ScholarshipServiceIntegration {
	public async retrieve(scholarshipId: string): Promise<Scholarship> {
		const tokenId = cookieHandler.retrieveTokenIdCookie();

		try {
			return await this.reconstitute(await this.v2Client.getById(scholarshipId, tokenId));
		} catch (error) {
			this.tryThrowAbstractedError(error as ResponseError);
			throw error;
		}
	}

	public async searchLatest(signal?: AbortSignal): Promise<ScholarshipSearchOutcome> {
		const tokenId = cookieHandler.retrieveTokenIdCookie();

		try {
			const searchOutcome = await this.abortable(this.v2Client.latest(tokenId), signal);
			const scholarships = await this.reconstituteAll(searchOutcome);

			return new ScholarshipSearchOutcome(
				scholarships,
				scholarships.length,
				true
			);
		} catch (error) {
			if ((error as DOMException).name === 'AbortError') {
				throw error;
			}

			this.tryThrowAbstractedError(error as ResponseError);

			throw error;
		}
	}

	public async searchByTitle(title: string, signal?: AbortSignal): Promise<ScholarshipSearchOutcome> {
		const tokenId = cookieHandler.retrieveTokenIdCookie();

		try {
			const searchOutcome = await this.abortable(this.v2Client.getByTitle(title, tokenId), signal);
			const scholarships = await this.reconstituteAll(searchOutcome);

			return new ScholarshipSearchOutcome(
				scholarships,
				scholarships.length,
				true
			);
		} catch (error) {
			if ((error as DOMException).name === 'AbortError') {
				throw error;
			}

			this.tryThrowAbstractedError(error as ResponseError);

			throw error;
		}
	}

	private abortable<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
		if (!signal) {
			return promise;
		}

		return new Promise((resolve, reject) => {
			signal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')), { once: true });
			promise.then(resolve, reject);
		});
	}

	public async reconstitute(source: IScholarship): Promise<Scholarship> {
		return new Scholarship(
			source.id,
			source.sourceUrl,
			source.foundAtUrl,
			await CountryPresenter.retrieveMultiple(source.destinationCountries),
			source.allDestinationCountries,
			await StatePresenter.retrieveMultiple(source.destinationStates),
			source.allDestinationStates,
			await CountryPresenter.retrieveMultiple(source.originCountries),
			source.allOriginCountries,
			await StatePresenter.retrieveMultiple(source.originStates),
			source.allOriginStates,
			await DisciplinePresenter.retrieveMultiple(source.disciplines),
			source.allDisciplines,
			source.grantDescription,
			source.grantAmount,
			source.grantCurrencyIso,
			source.deadlineType,
			source.deadlineIso !== null ? UTCDateTime.fromISOString(source.deadlineIso) : null,
			source.degrees,
			source.title,
			source.description,
			source.eligibility,
			source.benefits,
			source.applicationInfo,
			source.applicationFormUrl,
			source.providerOrganisationId,
			source.providerBodyId,
			source.minimumExperience,
			source.isAwardedMultipleTimes,
			source.applicationBasis,
			source.formats,
			source.types,
			source.ageMin,
			source.ageMax,
			source.gender,
			source.race,
			new Date(source.timestampCreatedIso),
			new Date(source.timestampModifiedIso),
			new Date(source.timestampModifiedByUserIso),
			source.summary,
			source.presence
		);
	}

	private async reconstituteAll(source: readonly IScholarship[]): Promise<readonly Scholarship[]> {
		return await Promise.all(source.map((s) => this.reconstitute(s)));
	}
}

export default new ScholarshipPresenter();
